import { useRef } from 'react'
import { ShieldCheck, AlertTriangle, FileSearch, ScanSearch, Loader2, Upload } from 'lucide-react'

interface ContractFlag {
  id: string
  category: 'pay_for_play' | 'enrollment_inducement' | 'guaranteed_deal' | 'missing_required_field'
  severity: 'critical' | 'warning' | 'info'
  excerpt: string
  rule: string
  recommendation: string
}

interface ContractScanResult {
  scannedAt: string
  overallResult: 'clean' | 'flagged' | 'pending' | 'not_scanned'
  flags: ContractFlag[]
}

interface Props {
  scanResults: ContractScanResult
  contractAvailable: boolean
  onScan: () => void
  isScanning: boolean
  onUpload?: (file: File) => Promise<void>
  isUploading?: boolean
}

function formatScanTime(isoString: string): string {
  const scanned = new Date(isoString)
  const now = new Date()
  const diffMs = now.getTime() - scanned.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins} min ago`
  if (diffHours < 24) return `${diffHours}h ago`
  return `${diffDays}d ago`
}

function categoryLabel(category: ContractFlag['category']): string {
  switch (category) {
    case 'pay_for_play':
      return 'Pay-for-play language'
    case 'enrollment_inducement':
      return 'Enrollment inducement'
    case 'guaranteed_deal':
      return 'Guaranteed deal clause'
    case 'missing_required_field':
      return 'Missing required field'
  }
}

function SeverityBadge({ severity }: { severity: ContractFlag['severity'] }) {
  if (severity === 'critical') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-[#FFA07A]/15 border border-[#FFA07A]/30 text-[#FFA07A]">
        Critical
      </span>
    )
  }
  if (severity === 'warning') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-[#F9DA4C]/15 border border-[#F9DA4C]/30 text-[#F9DA4C]">
        Warning
      </span>
    )
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-[#9CF0FB]/15 border border-[#9CF0FB]/30 text-[#9CF0FB]">
      Info
    </span>
  )
}

const ContractScanPanel = ({
  scanResults,
  contractAvailable,
  onScan,
  isScanning,
  onUpload,
  isUploading,
}: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const hasResult =
    scanResults.overallResult === 'clean' || scanResults.overallResult === 'flagged'
  const readyToScan = scanResults.overallResult === 'not_scanned' && contractAvailable

  return (
    <div className="bg-white/10 border border-white/10 rounded-2xl p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileSearch className="w-4 h-4 text-white/50" />
          <span className="text-white font-semibold text-sm">Agent Contract Scan</span>
        </div>
        {hasResult && (
          <div className="flex items-center gap-3">
            <span className="text-white/40 text-xs">
              Scanned {formatScanTime(scanResults.scannedAt)}
            </span>
            <button
              onClick={onScan}
              disabled={isScanning}
              className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-white/8 border border-white/15 hover:border-white/30 text-white/70 hover:text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ScanSearch className="w-3.5 h-3.5" />
              Re-scan
            </button>
          </div>
        )}
      </div>

      {/* Ready to scan — prominent CTA */}
      {readyToScan && (
        <button
          onClick={onScan}
          disabled={isScanning}
          className="w-full flex items-center justify-center gap-2.5 bg-[#C0FD72] hover:bg-[#d2ff9c] active:scale-[0.98] text-black font-semibold text-sm rounded-xl py-3.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ScanSearch className="w-4 h-4" />
          Scan Contract for Compliance Issues
        </button>
      )}

      {/* No contract uploaded — upload directly here */}
      {scanResults.overallResult === 'not_scanned' && !contractAvailable && (
        <div className="space-y-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0]
              if (!file || !onUpload) return
              await onUpload(file)
              if (fileInputRef.current) fileInputRef.current.value = ''
            }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="w-full flex items-center justify-center gap-2.5 bg-[#C0FD72] hover:bg-[#d2ff9c] active:scale-[0.98] text-black font-semibold text-sm rounded-xl py-3.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            {isUploading ? 'Uploading & Scanning…' : 'Upload Contract to Scan'}
          </button>
          <p className="text-white/30 text-xs text-center">PDF, DOC, or DOCX · max 20 MB</p>
        </div>
      )}

      {/* Pending */}
      {scanResults.overallResult === 'pending' && (
        <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-4">
          <Loader2 className="w-4 h-4 text-[#C0FD72]/60 animate-spin shrink-0" />
          <div>
            <p className="text-white/70 text-sm font-medium">Agent is reading the contract…</p>
            <p className="text-white/35 text-xs mt-0.5">
              Checking for prohibited language and extracting NIL Go fields
            </p>
          </div>
        </div>
      )}

      {/* Clean result */}
      {scanResults.overallResult === 'clean' && (
        <div className="bg-[#C0FD72]/10 border border-[#C0FD72]/30 rounded-xl px-4 py-4 space-y-3">
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="w-5 h-5 text-[#C0FD72] shrink-0" />
            <span className="text-[#C0FD72] text-sm font-semibold">
              Scan complete — no issues found
            </span>
          </div>
          <p className="text-white/60 text-xs leading-relaxed pl-7">
            No prohibited language detected. Contract appears valid under Bylaw 22. Your NIL Go
            form has been pre-filled with extracted details below.
          </p>
        </div>
      )}

      {/* Flagged result */}
      {scanResults.overallResult === 'flagged' && (
        <div className="space-y-3">
          <div className="flex items-start gap-3 bg-[#FFA07A]/8 border border-[#FFA07A]/20 rounded-xl px-4 py-3">
            <AlertTriangle className="w-5 h-5 text-[#FFA07A] shrink-0 mt-0.5" />
            <p className="text-[#FFA07A] text-sm font-medium">
              Agent flagged {scanResults.flags.length} item
              {scanResults.flags.length !== 1 ? 's' : ''} — review before submitting to NIL Go.
            </p>
          </div>

          <div className="space-y-3">
            {scanResults.flags.map((flag) => (
              <div
                key={flag.id}
                className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-2"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <SeverityBadge severity={flag.severity} />
                  <span className="text-white/80 text-sm font-medium">
                    {categoryLabel(flag.category)}
                  </span>
                </div>

                <div className="bg-white/5 rounded px-3 py-2">
                  <p className="text-white/60 text-xs font-mono leading-relaxed">{flag.excerpt}</p>
                </div>

                <div className="space-y-1">
                  <p className="text-white/50 text-xs">
                    <span className="text-white/30">Rule: </span>
                    {flag.rule}
                  </p>
                  <p className="text-white/50 text-xs">
                    <span className="text-white/30">Action: </span>
                    {flag.recommendation}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default ContractScanPanel
